import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingPaarlPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Paarl Professional Painters - Residential & Commercial Painting | Winelands Specialists',
    description: 'Expert painting services in Paarl, Wellington, and Franschhoek. Specialized in residential, commercial, and heritage property painting. Over 16 years serving the Winelands with quality craftsmanship.',
    keywords: 'painters Paarl, house painters Paarl, residential painting Paarl, commercial painting, heritage painting, winelands painters',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Paarl',
          description: 'Professional painting services in Paarl and Winelands',
          address: 'Paarl, Western Cape',
          priceRange: '$$',
          serviceArea: ['Paarl', 'Wellington', 'Franschhoek', 'Dal Josafat', 'Mbekweni']
        }),
        generateServiceSchema({
          name: 'Winelands Painting Services',
          description: 'Residential, commercial, and heritage painting',
          provider: 'Painters Paarl',
          areaServed: ['Paarl', 'Wellington', 'Franschhoek'],
          serviceType: ['Residential Painting', 'Commercial Painting', 'Heritage Painting']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Painters Paarl",
    location: "Paarl, Western Cape",
    website: "https://painterspaarl.co.za/",
    heroImage: "https://public.readdy.ai/ai/img_res/7ed0419d5d2a170d9940a103707f76e7.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=beautifully%20painted%20house%20exterior%20in%20Paarl%20wine%20country%20with%20mountain%20views%20Cape%20Dutch%20architecture%20white%20walls%20dark%20trim%20professional%20finish&width=800&height=600&seq=gallery-paarl1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=interior%20room%20painting%20project%20in%20Paarl%20home%20elegant%20living%20space%20with%20feature%20wall%20modern%20furniture%20natural%20lighting%20professional%20paint%20finish&width=800&height=600&seq=gallery-paarl2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=exterior%20house%20painting%20in%20Paarl%20with%20scaffolding%20professional%20painters%20working%20on%20facade%20mountain%20backdrop%20wine%20country%20setting&width=800&height=600&seq=gallery-paarl3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=roof%20painting%20project%20in%20Paarl%20residential%20property%20with%20mountain%20views%20professional%20coating%20application%20clean%20finish%20wine%20country%20setting&width=800&height=600&seq=gallery-paarl4&orientation=landscape"
    ],
    services: [
      {
        title: "Interior Painting",
        description: "Transform your Paarl home's interior with our expert painting services. We create beautiful, durable finishes that enhance your living spaces and reflect the character of this historic wine country town.",
        features: ["Living rooms & bedrooms", "Kitchens & bathrooms", "Feature walls & accents", "Ceiling painting", "Trim & woodwork", "Color consultation"]
      },
      {
        title: "Exterior Painting",
        description: "Protect and beautify your property's exterior with our professional painting services. We specialize in both modern homes and heritage Cape Dutch architecture, using premium paints suited to Paarl's climate.",
        features: ["House facades & walls", "Cape Dutch gables", "Fascias & soffits", "Garden walls & fences", "Garage doors", "Pressure washing"]
      },
      {
        title: "Roof Painting",
        description: "Extend the life of your roof with our specialized painting and coating services. Our waterproofing solutions protect against Paarl's hot summers and wet winters while improving your property's appearance.",
        features: ["Tile roof painting", "Metal roof coating", "Waterproofing treatments", "Leak repairs", "Gutter painting", "Heat-reflective coatings"]
      }
    ],
    serviceAreas: ["Paarl", "Paarl East", "Paarl Central", "Dal Josafat", "Mbekweni", "Wellington", "Franschhoek", "Simondium", "Klapmuts"],
    about: "Painters Paarl is the Boland region's premier painting company, serving Paarl and surrounding wine country areas for over 17 years. We have established ourselves as the most trusted painting contractors in the region, known for our expertise in both modern residential properties and historic Cape Dutch architecture.",
    aboutExtended: [
      "Our team of highly skilled painters brings extensive experience in handling the diverse architectural styles found throughout Paarl, from contemporary wine estate homes to beautifully preserved heritage buildings. We understand the unique requirements of each property type and use only premium quality paints and materials specifically selected for the Boland climate.",
      "What distinguishes Painters Paarl is our deep appreciation for the region's architectural heritage combined with expertise in modern painting techniques. Whether you're restoring a historic Cape Dutch homestead, refreshing a wine farm building, or painting a new development property, we bring the same level of professional care and attention to detail.",
      "We pride ourselves on building lasting relationships with our clients through transparent communication, reliable service, and exceptional workmanship. Our comprehensive approach includes thorough surface preparation, expert color consultation, premium paint application, and meticulous cleanup. Every project is completed to the highest standards with full respect for your property and schedule."
    ],
    ourApproach: [
      "Free on-site consultation and detailed quotation",
      "Comprehensive surface preparation and repairs",
      "Premium quality paints suited to Boland climate",
      "Expert application by certified painters",
      "Heritage property specialists",
      "Minimal disruption to your routine",
      "Thorough cleanup and waste removal",
      "Final inspection and warranty coverage"
    ],
    whyChoose: [
      "17+ years serving Paarl and Boland",
      "Fully licensed and insured",
      "Cape Dutch architecture specialists",
      "Premium quality materials",
      "Free color consultation",
      "Heritage restoration expertise",
      "Competitive pricing",
      "Punctual and reliable",
      "Wine estate experience",
      "Eco-friendly options available"
    ],
    testimonials: [
      {
        name: "Francois du Toit",
        rating: 5,
        text: "Outstanding work on our wine estate! The team was professional, respectful of our operations, and the quality is exceptional. They really understand Cape Dutch architecture and the results are stunning."
      },
      {
        name: "Maria Lombard",
        rating: 5,
        text: "We've used Painters Paarl for multiple projects over the years. They never disappoint! Professional, reliable, and the attention to detail is impressive. Best painters in the Boland region."
      },
      {
        name: "Johan Steyn",
        rating: 5,
        text: "Excellent service from start to finish! They completed our home renovation ahead of schedule and the quality exceeded our expectations. Great value for money and wonderful customer service."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Painters Paarl</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Painters Paarl</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <p className="text-gray-700 text-lg mb-4">We proudly serve the following areas in the Paarl and Boland region:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Professional Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">We offer a comprehensive range of painting services tailored to meet the unique needs of Paarl and Boland properties.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">Paarl, Wellington, Franschhoek</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 7:30AM-5PM</p>
                    <p className="text-gray-600">Sat: 8AM-12PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Paarl</li>
                <li>Wellington</li>
                <li>Franschhoek</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
